/**
 * Log tahap giliran suara (klien, #62): setiap delegasi GPT-Live yang diterima
 * dicatat beserta nasibnya (dijawab / diabaikan + alasan / gagal / fallback).
 * Hanya nama tahap, waktu, dan alasan — TANPA isi ucapan (bukan PII). Ring
 * buffer di memori tab; dibaca lewat `getVoiceStageLog()` untuk diagnosis.
 */

export type VoiceStage =
	| "delegation_received"
	| "delegation_ignored"
	| "delegation_answered"
	| "delegation_failed"
	| "delegation_late_bound"
	| "fallback_started"
	| "fallback_skipped"
	| "fallback_scheduled"
	| "fallback_cancelled"
	| "speech_detected"
	| "output_delta"
	| "barge_in"
	| "session_lost"
	| "event_unhandled";

export interface VoiceStageEntry {
	stage: VoiceStage;
	/** performance.now() saat dicatat. */
	at: number;
	/** Jam dinding (epoch ms) — untuk dicocokkan dengan Server Log. */
	wall: number;
	/** Alasan singkat (mis. "echo", "hesitation"); tidak pernah berisi ucapan. */
	reason?: string;
}

export type VoiceStageLogger = (stage: VoiceStage, reason?: string) => void;

/** Banyak entri yang disimpan sebelum yang tertua dibuang. */
export const VOICE_STAGE_LOG_MAX = 100;

const entries: VoiceStageEntry[] = [];

/** Catat satu tahap (mengganti `console.*`: log klien dilarang di kode produksi). */
export const logVoiceStage: VoiceStageLogger = (stage, reason) => {
	entries.push({ stage, at: performance.now(), wall: Date.now(), reason });
	if (entries.length > VOICE_STAGE_LOG_MAX) entries.shift();
};

/** Salinan log tahap terbaru (tertua dulu). */
export const getVoiceStageLog = (): readonly VoiceStageEntry[] => [...entries];

export const clearVoiceStageLog = () => {
	entries.length = 0;
};

/** Panjang maksimal nama tipe event yang ditulis ke log. */
export const EVENT_TYPE_LOG_MAX = 64;
/** Banyak tipe tak dikenal yang dicatat; sisanya diabaikan agar ring buffer tidak banjir. */
export const UNHANDLED_TYPE_LOG_MAX = 20;

/**
 * Nama tipe event yang aman untuk log: hanya karakter penyusun nama protokol dan
 * dipangkas, supaya payload tak terduga tidak ikut masuk ke log yang dijanjikan
 * tanpa isi ucapan.
 */
export function safeEventType(type: string): string {
	const clean = type.trim();
	if (!clean) return "(kosong)";
	// Menyaring karakter satu per satu masih bisa menyisakan pecahan kata, jadi
	// nama yang tidak berbentuk pengenal protokol dibuang seluruhnya.
	if (!/^[\w.:-]+$/.test(clean)) return "(tidak dikenal)";
	return clean.slice(0, EVENT_TYPE_LOG_MAX);
}

/**
 * Pencatat tipe event GPT-Live yang tidak ditangani controller: satu entri per
 * tipe. Event semacam ini bisa datang beruntun, jadi pengulangannya dibuang agar
 * tidak mendorong keluar entri lain dari ring buffer.
 */
export function createUnhandledEventLog(
	log: VoiceStageLogger = logVoiceStage,
): (type: string) => void {
	const seen = new Set<string>();
	return (type) => {
		const name = safeEventType(type);
		if (seen.has(name) || seen.size >= UNHANDLED_TYPE_LOG_MAX) return;
		seen.add(name);
		log("event_unhandled", name);
	};
}

/** Teks log tahap untuk disalin admin: satu baris per tahap, jam WITA-lokal browser, tanpa isi ucapan. */
export function formatVoiceStageLog(
	list: readonly VoiceStageEntry[] = entries,
): string {
	return list
		.map((e) => {
			const time = new Date(e.wall).toISOString().slice(11, 23);
			return `${time}Z ${e.stage}${e.reason ? ` (${e.reason})` : ""}`;
		})
		.join("\n");
}
